/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    BUFFER_THRESHOLD_OFFSET,
    DEFAULT_CONTENT_DURATION_IN_MILLISECONDS,
    DEFAULT_SEEK_RANGE_IN_MILLISECONDS,
    HALF_HOUR_IN_MILLISECONDS,
    MILLISECONDS_IN_A_MINUTE,
    MILLISECONDS_IN_A_SECOND,
    MIN_CACHE_IN_MILLISECONDS,
    PROGRESS_BAR_LENGTH,
    SECONDS_IN_A_MINUTE,
    TICK_DURATION,
    TRICK_PLAY_DISPLAY_TIME,
    TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME,
} from '../../../config';
import { DeviceFeatures } from '../../../core/device/DeviceFeatures';
import { DimensionScaler } from '../../../core/device/DimensionScaler';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { QoEAppLogger } from '../../../core/logging/qoe/QoEAppLogger';
import { VuLogger } from '../../../core/logging/VULogger';
import { PlaybackContentType } from '../../../core/playback/enums';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { DateTime } from '../../../core/utils';
import { MetadataType } from '../../../core/videoplayer/enums';
import { TrickplayImpl } from '../../../core/videoplayer/impls';
import { AiringPreviewUtils } from '../../../core/videoplayer/utils';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { videoPlayerIsMediaButtonPressedAction } from '../../../store/videoplayer/videoplayer.actions';
import { toggleIsPlaying } from '../../../store/videoplayer/videoplayer.reducers';
import { updateTrickPlayMenuTimer } from '../../../store/videoplayerui/infobanner/infobanner.reducers';
import {
    PlaybackState,
    TrickPlayType,
} from '../../../store/videoplayerui/infobanner/PlaybackTypes';
import { MediaButtonsPanel } from './MediaButtons';
import { TrickplayButtonProps } from './TrickplayButtonProps';
import TrickplayOsd from './TrickplayOsd';

import './TrickplayBanner.scss';

const TrickplayBanner: FunctionComponent<TrickplayButtonProps> = ({ channel }): JSX.Element => {
    const dispatch = useTivoDispatch();
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    const { airingOffers, offerPreview } = oVideoPlaybackController.getMetadata();
    const { playbackState, mediaButtonPressed } = useTivoSelector(
        (state: RootState) => state.videoPlayer,
    );

    // setting default as true for the case of NDVR because we do not get channel
    const isSubscribed = channel?.isSubscribed ?? true;
    const {
        allowRewind = false,
        allowPause = false,
        allowFastForward = false,
    } = oVideoPlaybackController.trickModeRestrictions() || {};
    const dataFormatter = new AiringPreviewUtils(airingOffers, offerPreview);
    const isAvailableInSocu = dataFormatter.getIsAvailableInSocu();
    const isRecordingInProgress = oVideoPlaybackController.isRecordingInProgress();
    const showRecordIcon = oVideoPlaybackController.isNdvrLive() || isRecordingInProgress;

    const isSocuPlaybackEnabled = isAvailableInSocu && DeviceFeatures.isSocuEnabled();
    const isTrickplayEnabled = oVideoPlaybackController.isTrickplayEnabled();
    // useRefs
    const commonOffset = useRef(0);
    const tickmarkRef = useRef(null);

    // useStates
    const getCurrentTime = () => {
        const currentTime = oVideoPlaybackController.getPlayerCurrentTime();
        const start =
            oVideoPlaybackController.isStartOver() ||
            oVideoPlaybackController.isNdvrLive() ||
            oVideoPlaybackController.isLiveWithRecording()
                ? oVideoPlaybackController.getSeekRange().start
                : 0;
        return (currentTime - start) * MILLISECONDS_IN_A_SECOND;
    };

    const [currentTime, setCurrentTime] = useState(getCurrentTime() ?? new Date().getTime());

    const calculateBarStartEndTime = () => {
        const { start, end } = oVideoPlaybackController.getSeekRange();
        const cacheSize =
            (end - start) * MILLISECONDS_IN_A_SECOND || DEFAULT_SEEK_RANGE_IN_MILLISECONDS;

        if (oVideoPlaybackController.isNdvrLive()) {
            const duration =
                (oVideoPlaybackController.getMetadata().offerPreview?.contentPreview
                    ?.recordingDuration ??
                    oVideoPlaybackController.getMetadata().offerPreview?.contentPreview?.duration ??
                    0) * MILLISECONDS_IN_A_SECOND;
            return { barStartTime: 0, barEndTime: Math.max(duration, cacheSize) };
        }
        if (oVideoPlaybackController.isStartOver()) {
            const showingDuration =
                (oVideoPlaybackController.getMetadata().watchAction?.kernel?.duration || 0) *
                MILLISECONDS_IN_A_SECOND;
            return { barStartTime: 0, barEndTime: Math.max(showingDuration, cacheSize) };
        }
        if (oVideoPlaybackController.isLiveWithRecording()) {
            const showingDuration = (dataFormatter.getDuration() || 0) * MILLISECONDS_IN_A_SECOND;
            return { barStartTime: 0, barEndTime: Math.max(showingDuration, cacheSize) };
        }
        if (
            oVideoPlaybackController.isCatchUp() ||
            oVideoPlaybackController.isNdvrRecorded() ||
            oVideoPlaybackController.isVod()
        ) {
            return { barStartTime: 0, barEndTime: cacheSize };
        }
        let finalCacheSize =
            cacheSize <= MIN_CACHE_IN_MILLISECONDS ? HALF_HOUR_IN_MILLISECONDS : cacheSize;
        /*
         * Rounding off the finalCacheSize to perfect minutes, so that few seconds or milliseconds
         * would not add extra 30 minutes to the endTime due its Math.ceil calculation
         */
        finalCacheSize =
            Math.trunc(finalCacheSize / MILLISECONDS_IN_A_MINUTE) * MILLISECONDS_IN_A_MINUTE;

        // Subtract CACHE_SIZE from the current time and round DOWN to the nearest half hour.
        let startTime = Date.now() - finalCacheSize;
        startTime = Math.floor(startTime / HALF_HOUR_IN_MILLISECONDS) * HALF_HOUR_IN_MILLISECONDS;

        // Start Time plus CACHE_SIZE plus 30 minutes, rounded UP to the nearest half hour.
        let endTime = startTime + finalCacheSize + HALF_HOUR_IN_MILLISECONDS;
        endTime = Math.ceil(endTime / HALF_HOUR_IN_MILLISECONDS) * HALF_HOUR_IN_MILLISECONDS;
        return { barStartTime: startTime, barEndTime: endTime };
    };

    const [barStartEndTime, setBarStartEndTime] = useState(calculateBarStartEndTime());

    const { barStartTime, barEndTime } = barStartEndTime;

    const getCacheStartTime = () => {
        return oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            oVideoPlaybackController.isVod()
            ? 0
            : oVideoPlaybackController.getSeekRange().start * MILLISECONDS_IN_A_SECOND;
    };

    const bufferStartTime = getCacheStartTime() || 0;

    const getCacheEndTime = () => {
        const { start, end } = oVideoPlaybackController.getSeekRange();
        const startTime =
            oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            oVideoPlaybackController.isVod()
                ? start
                : 0;
        return (end - startTime) * MILLISECONDS_IN_A_SECOND;
    };

    const bufferEndTime = getCacheEndTime() || 0;

    const calculateCurrentPosition = () => {
        return oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            oVideoPlaybackController.isVod()
            ? Number(
                  (
                      (DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) *
                          currentTime) /
                      (barEndTime || DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                  ).toFixed(3),
              )
            : Number(
                  (
                      (DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) *
                          (currentTime - barStartTime)) /
                      (barStartTime && barEndTime
                          ? barEndTime - barStartTime
                          : DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                  ).toFixed(3),
              );
    };

    const calculateCacheStartPosition = () => {
        if (!allowRewind && !allowFastForward) return 0;
        if (
            oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            oVideoPlaybackController.isVod()
        )
            return 0;
        if (barStartTime && barEndTime && bufferStartTime) {
            const startPositionTime =
                allowFastForward && !allowRewind
                    ? currentTime - barStartTime
                    : bufferStartTime - barStartTime;
            return Number(
                (
                    (DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) *
                        startPositionTime) /
                    (barEndTime - barStartTime || DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                ).toFixed(3),
            );
        }
        return 0;
    };

    const calculateCacheEndPosition = () => {
        if (!allowFastForward && !allowRewind) return 0;
        if (
            oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            (oVideoPlaybackController.isVod() && barEndTime && bufferEndTime)
        )
            return Number(
                (
                    (DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) *
                        bufferEndTime) /
                    (barEndTime || DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                ).toFixed(3),
            );
        if (barStartTime && barEndTime && bufferEndTime) {
            const endPositionTime =
                allowRewind && !allowFastForward
                    ? currentTime - barStartTime
                    : bufferEndTime - barStartTime;
            return Number(
                (
                    (DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) *
                        endPositionTime) /
                    (barEndTime - barStartTime || DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                ).toFixed(3),
            );
        }
        return 0;
    };

    const [currentPosition, setCurrentPosition] = useState(calculateCurrentPosition());
    const [cacheStartPosition, setcacheStartPosition] = useState(calculateCacheStartPosition());
    const [cacheEndPosition, setcacheEndPosition] = useState(calculateCacheEndPosition());

    // Other supporting functions

    const getFormatedCurrentTime = () => {
        if (
            oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            oVideoPlaybackController.isVod()
        ) {
            if (currentTime < 0) return DateTime.convertToReadableRounded(0);
            return DateTime.convertToReadableRounded(currentTime);
        }
        return new DateTime(getCurrentTime()).to12HourClock();
    };

    const getFormatedBarStartTime = () => {
        if (
            oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            oVideoPlaybackController.isVod()
        )
            return '';
        return (barStartTime && new DateTime(barStartTime).to12HourClock()) || '';
    };

    const getFormatedBarEndTime = () => {
        if (
            oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            oVideoPlaybackController.isVod()
        ) {
            return (barEndTime && DateTime.convertToReadableRounded(barEndTime)) || '';
        }
        return (barEndTime && new DateTime(barEndTime).to12HourClock()) || '';
    };

    const isBufferThresholdReachedEnd = (): boolean => {
        const { end } = oVideoPlaybackController.getSeekRange();
        const playerCurrentTime = oVideoPlaybackController.getPlayerCurrentTime();
        return end === playerCurrentTime || end - playerCurrentTime < BUFFER_THRESHOLD_OFFSET;
    };

    const isBufferThresholdReachedStart = (): boolean => {
        const { start } = oVideoPlaybackController.getSeekRange();
        const playerCurrentTime = oVideoPlaybackController.getPlayerCurrentTime();
        return start === playerCurrentTime || playerCurrentTime - start < BUFFER_THRESHOLD_OFFSET;
    };

    const isBufferThresholdReached = (): boolean => {
        return isBufferThresholdReachedStart() || isBufferThresholdReachedEnd();
    };

    const setTrickplayModeIcon = useCallback(() => {
        switch (playbackState) {
            case PlaybackState.RW3:
                return <img className="media-rw3-icon" />;
            case PlaybackState.RW2:
                return <img className="media-rw2-icon" />;
            case PlaybackState.RW1:
                return <img className="media-rw1-icon" />;
            case PlaybackState.PAUSE:
                return <img className="media-pause-icon" />;
            case PlaybackState.PLAY:
                return <img className="media-play-icon" />;
            case PlaybackState.FF3:
                return <img className="media-ff3-icon" />;
            case PlaybackState.FF2:
                return <img className="media-ff2-icon" />;
            case PlaybackState.FF1:
                return <img className="media-ff1-icon" />;
            default:
                return <></>;
        }
    }, [playbackState]);

    const checkConditions = () => {
        return (
            isSubscribed &&
            oVideoPlaybackController.isPlaybackStarted() &&
            oVideoPlaybackController.isNetworkConnected()
        );
    };

    const calculateNumberOfTicks = () => {
        const oTrickplayImpl = new TrickplayImpl(
            oVideoPlaybackController.getPlayable(),
            oVideoPlaybackController.getPlaybackIntervalInfo(),
            oVideoPlaybackController.getMetadata(),
        );
        const duration = oTrickplayImpl.getProgramDuration() / SECONDS_IN_A_MINUTE;
        return duration < TICK_DURATION
            ? 1
            : Math.ceil(duration / oTrickplayImpl.getTickmarkDuration(duration));
    };

    const numOfTicks = useMemo(() => {
        return calculateNumberOfTicks();
    }, [barEndTime, barStartTime]);

    const renderTickmarks = (index: number, offset: number) => (
        <div key={`tickmark-${index + 1}`} className={'tickmark'} style={{ left: offset }} />
    );

    // Default tickmark to show until playback first frame renders.
    const getDefaultTickmarks = () => {
        const offset =
            commonOffset.current +
            DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false);
        return renderTickmarks(0, offset);
    };

    const calculateDynamicTickMarkOffset = (
        index: number,
        oTrickplayImpl: TrickplayImpl,
    ): number => {
        if (
            oVideoPlaybackController.isSocu() ||
            oVideoPlaybackController.isNdvr() ||
            oVideoPlaybackController.isLiveWithRecording() ||
            oVideoPlaybackController.isVod()
        ) {
            const duration = oTrickplayImpl.getProgramDuration() / SECONDS_IN_A_MINUTE;
            const tickOffset =
                DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) / duration;
            const remainingTime = duration % oTrickplayImpl.getTickmarkDuration(duration);
            return index === numOfTicks - 1 && remainingTime !== 0
                ? commonOffset.current +
                      (index * oTrickplayImpl.getTickmarkDuration(duration) + remainingTime) *
                          tickOffset
                : commonOffset.current +
                      (index + 1) * oTrickplayImpl.getTickmarkDuration(duration) * tickOffset;
        }
        return (
            commonOffset.current +
            (index + 1) *
                (DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) /
                    numOfTicks)
        );
    };

    const getTickmarks = () => {
        const oTrickplayImpl = new TrickplayImpl(
            oVideoPlaybackController.getPlayable(),
            oVideoPlaybackController.getPlaybackIntervalInfo(),
            oVideoPlaybackController.getMetadata(),
        );
        if (!checkConditions()) return getDefaultTickmarks();
        return Array(numOfTicks)
            .fill(undefined)
            .map((_value, index) => {
                const offset = calculateDynamicTickMarkOffset(index, oTrickplayImpl);
                return renderTickmarks(index, offset);
            });
    };

    const checkConditionsWithoutPlay = () => {
        return (
            isSubscribed &&
            // TODO: Enable button if player state LOADED,
            // change as per player state listener approach.
            oVideoPlaybackController.isNetworkConnected()
        );
    };

    const checkStartFromBufferButton = () => {
        if (checkConditionsWithoutPlay() && isSocuPlaybackEnabled) return false;
        return allowRewind && checkConditions() ? isBufferThresholdReachedStart() : true;
    };

    const checkIsPlayPauseButtonDisabled = () => {
        if (checkConditions() && (allowPause || isTrickplayEnabled)) return false;
        return true;
    };

    const checkIsForwardButtonDisabled = () => {
        return checkConditions() && !isBufferThresholdReachedEnd() ? !allowFastForward : true;
    };

    const checkIsRewindButtonDisabled = () => {
        return checkConditions() && !isBufferThresholdReachedStart() ? !allowRewind : true;
    };

    const checkIsLiveButtonDisabled = () => {
        return (
            oVideoPlaybackController.getPlayable()?.playbackContentType === PlaybackContentType.LIVE
        );
    };

    const getMediaButtonRestrictionString = () => {
        switch (mediaButtonPressed.type) {
            case TrickPlayType.REPLAY:
                return Locale.getString(LocaleKeys.LIVE_TV_REPLAY_NOT_ALLOWED);
            case TrickPlayType.RW:
                return Locale.getString(LocaleKeys.LIVE_TV_REWIND_NOT_ALLOWED);
            case TrickPlayType.PAUSE:
                return Locale.getString(LocaleKeys.LIVE_TV_PAUSE_NOT_SUPPORTED_MESSAGE);
            case TrickPlayType.FF:
                return Locale.getString(LocaleKeys.LIVE_TV_FAST_FORWARD_NOT_ALLOWED);
            case TrickPlayType.ADVANCE:
                return Locale.getString(LocaleKeys.LIVE_TV_ADVANCE_NOT_ALLOWED);
            default:
                return '';
        }
    };

    const refreshCurrentTime = () => {
        const playerCurrentTime = getCurrentTime() ?? Date.now();
        setCurrentTime(playerCurrentTime);
    };

    const renderCachePositionAndTime = () => {
        const progressBarStyle =
            cacheStartPosition || cacheEndPosition
                ? {
                      left: cacheStartPosition,
                      width: Math.abs(cacheEndPosition - cacheStartPosition),
                  }
                : {};
        return (
            <>
                <div className="media-progress-bar-cache" style={progressBarStyle} />
                <div className="media-current-time-tickmark" style={{ left: currentPosition }} />
            </>
        );
    };

    const renderProgressBar = () => {
        return (
            <div className="media-progress-bar-container">
                {checkConditions() && renderCachePositionAndTime()}
                <span className="media-start-time">
                    {checkConditions() ? getFormatedBarStartTime() : ''}
                </span>
                <div ref={tickmarkRef} key={'tickmark'} className={'tickmark'}></div>
                {getTickmarks()}
                <div className="media-progress-bar-empty"></div>
                <span className="media-end-time">
                    {checkConditions() ? getFormatedBarEndTime() : ''}
                </span>
            </div>
        );
    };

    const renderMediaCurrentTme = () => {
        return (
            <div className="media-current-time-container" style={{ left: currentPosition }}>
                {showRecordIcon ? <img className="media-recording-icon" /> : null}
                <span className="media-current-time">{getFormatedCurrentTime()}</span>
                {setTrickplayModeIcon()}
            </div>
        );
    };

    const renderMediaRestrictions = () => {
        return (
            <span className="media-pause-not-suppoted-container">
                {mediaButtonPressed.isMediaButtonPressed && (
                    <>
                        <img className="media-pause-not-suppoted-icon" />
                        <span className="media-pause-not-suppoted-text">
                            {getMediaButtonRestrictionString()}
                        </span>
                    </>
                )}
            </span>
        );
    };

    const UpdateBarStartEndTimeIfRequired = () => {
        const newBarStartEnd = calculateBarStartEndTime();
        if (
            barStartEndTime.barEndTime !== newBarStartEnd.barEndTime ||
            barStartEndTime.barStartTime !== newBarStartEnd.barStartTime
        ) {
            setBarStartEndTime(newBarStartEnd);
        }
    };

    // useEffects

    useEffect(() => {
        if (tickmarkRef && tickmarkRef.current) {
            commonOffset.current = parseInt(
                window.getComputedStyle(tickmarkRef.current).getPropertyValue('left'),
                10,
            );
        }
        setBarStartEndTime(calculateBarStartEndTime());
        refreshCurrentTime();
    }, []);

    /**
     * This use effect will be called when the buffer end time changes(Live, StartOver).
     * Playback start time and end time will be changed based on
     * the program duration from SOCU offer and the duration from the player.
     * If the program duration time from the SOCU offer is less than
     * the duration from the player, then show the duration from the SOCU offer;
     * otherwise, show the duration from the player.
     */
    useEffect(() => {
        const cacheEndTime = bufferEndTime || Date.now();
        if (cacheEndTime > barEndTime) {
            setBarStartEndTime(calculateBarStartEndTime());
        }
    }, [bufferEndTime, barEndTime]);

    useEffect(() => {
        const timerId = setInterval(
            refreshCurrentTime,
            isTrickplayEnabled
                ? TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME
                : MILLISECONDS_IN_A_SECOND,
        );
        return () => {
            clearInterval(timerId);
        };
    }, [isTrickplayEnabled]);

    useEffect(() => {
        setCurrentPosition(calculateCurrentPosition());
        setcacheStartPosition(calculateCacheStartPosition());
        setcacheEndPosition(calculateCacheEndPosition());
        UpdateBarStartEndTimeIfRequired();

        if (oVideoPlaybackController.isLive()) {
            const programStartTime = new Date(dataFormatter.getStartTime()).getTime();
            const programEndTime = dataFormatter.getEndTime().getTime();
            const playable = oVideoPlaybackController.getPlayable();
            const currentTimeForAiring =
                oVideoPlaybackController.getPlayerCurrentTime() * MILLISECONDS_IN_A_SECOND;
            if (!programEndTime || !programStartTime) return;
            if (
                playable &&
                oVideoPlaybackController.isPlaybackStarted() &&
                (currentTimeForAiring > programEndTime || currentTimeForAiring < programStartTime)
            ) {
                oVideoPlaybackController.prepareMetadataByType(
                    playable,
                    MetadataType.AIRING,
                    currentTimeForAiring,
                );
                QoEAppLogger.logProgramEnded(
                    oVideoPlaybackController.getPlayerStats(),
                    oVideoPlaybackController.getPlayerCurrentTime(),
                    oVideoPlaybackController.getSeekRange(),
                );
                VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.END_OF_CONTENT);
            }
        }
    }, [currentTime]);

    useEffect(() => {
        if (isTrickplayEnabled && isBufferThresholdReached()) {
            if (playbackState !== PlaybackState.PLAY) dispatch(toggleIsPlaying());
            refreshCurrentTime();
            dispatch(updateTrickPlayMenuTimer());
        }
    }, [isBufferThresholdReached()]);

    useEffect(() => {
        let pauseNotSupportTimer: any = null;
        if (mediaButtonPressed.isMediaButtonPressed) {
            pauseNotSupportTimer = setTimeout(() => {
                dispatch(
                    videoPlayerIsMediaButtonPressedAction({
                        type: TrickPlayType.NONE,
                        isMediaButtonPressed: false,
                    }),
                );
            }, TRICK_PLAY_DISPLAY_TIME);
        }
        return () => {
            clearTimeout(pauseNotSupportTimer);
        };
    }, [mediaButtonPressed]);

    return (
        <>
            <div className="media-container" data-testid="media-buttons-container">
                <div className="media-dropshadow-down"></div>
                {renderProgressBar()}
                <div className="media-trick-play-container">
                    {checkConditions() && !mediaButtonPressed.isMediaButtonPressed
                        ? renderMediaCurrentTme()
                        : renderMediaRestrictions()}
                </div>
                <MediaButtonsPanel
                    isAvailableInSocu={isAvailableInSocu}
                    isPlayPauseButtonDisabled={checkIsPlayPauseButtonDisabled()}
                    isForwardButtonDisabled={checkIsForwardButtonDisabled()}
                    isRewindButtonDisabled={checkIsRewindButtonDisabled()}
                    isAdvanceButtonDisabled={checkIsForwardButtonDisabled()}
                    isReplayButtonDisabled={checkIsRewindButtonDisabled()}
                    isGoToLiveButtonDisabled={
                        allowFastForward && checkConditions() ? isBufferThresholdReachedEnd() : true
                    }
                    isStartFromBufferButtonDisabled={checkStartFromBufferButton()}
                    isLiveButtonDisabled={checkIsLiveButtonDisabled()}
                />
            </div>
            {checkConditions() && isTrickplayEnabled && <TrickplayOsd />}
        </>
    );
};

export default TrickplayBanner;
